import type {
  PlayerPilotRating,
  PlayerPilotRatingHistory,
  PlayerPilotRatedMatchFeed,
  RatingPilotContext,
  RatingPilotEvidenceState,
  RatingPilotHistorySnapshot,
  SquadMemberPilotRating,
} from '../types/ratingPilot';
import { apiClient } from './api';

const EVIDENCE_LABELS: Record<RatingPilotEvidenceState, string> = {
  ESTIMATED: 'Early estimate',
  PROJECTED: 'Building history',
  CONFIRMED: 'Strong evidence',
  ESTABLISHED: 'Well supported',
};

export type RatingPilotSurfaceState =
  | 'loading'
  | 'offline'
  | 'unavailable'
  | 'paused'
  | 'failed'
  | 'stale_offline'
  | 'stale_failed'
  | 'ready';

export type RatingScaleBand = {
  markerPercent: number;
  intervalLeftPercent: number | null;
  intervalWidthPercent: number | null;
};

export type RatingJourneyPoint = {
  throughPeriod: string;
  value: number;
  restated: boolean;
};

export const RATING_SCALE_MIN = 1;
export const RATING_SCALE_MAX = 7;
export const RATING_SCALE_COPY = 'TARK uses its own 1.00-7.00 scale. Higher numbers reflect stronger results in this pilot. It is not a USTA, UTR, WTN or NTRP equivalent.';

type RatingPilotSurfaceInput = {
  loading: boolean;
  rating?: PlayerPilotRating;
  error?: unknown;
  offline?: boolean;
  paused?: boolean;
};

export async function getMyPilotRating(squadId?: number): Promise<PlayerPilotRating> {
  const response = await apiClient.get<PlayerPilotRating>('/api/v1/web/ratings/pilot/me', {
    params: squadId == null ? undefined : { squadId },
  });
  return response.data;
}

export async function getMyPilotRatingHistory(squadId: number): Promise<PlayerPilotRatingHistory> {
  const response = await apiClient.get<PlayerPilotRatingHistory>('/api/v1/web/ratings/pilot/history', {
    params: { squadId },
  });
  return response.data;
}

export async function getMyPilotRatedMatches(squadId: number): Promise<PlayerPilotRatedMatchFeed> {
  const response = await apiClient.get<PlayerPilotRatedMatchFeed>('/api/v1/web/ratings/pilot/matches', {
    params: { squadId },
  });
  return response.data;
}

export async function getSquadMemberPilotRating(squadId: number, targetUserId: number): Promise<SquadMemberPilotRating> {
  const response = await apiClient.get<SquadMemberPilotRating>(`/api/v1/web/ratings/pilot/players/${targetUserId}`, {
    params: { squadId },
  });
  return response.data;
}

export async function getRatingPilotContexts(): Promise<RatingPilotContext[]> {
  const response = await apiClient.get<RatingPilotContext[]>('/api/v1/web/ratings/pilot/contexts');
  return response.data;
}

export function ratingPilotHeadline(value: PlayerPilotRating): string | null {
  if (!value.available) return null;
  if (value.displayKind === 'NUMERIC' && Number.isFinite(value.displayRating)) {
    return Number(value.displayRating).toFixed(2);
  }
  if (value.displayKind === 'BAND' && value.displayBand?.trim()) {
    return value.displayBand.trim();
  }
  return null;
}

export function squadMemberPilotRatingHeadline(value: SquadMemberPilotRating): string | null {
  if (!value.available) return null;
  if (value.displayKind === 'NUMERIC' && Number.isFinite(value.displayRating)) {
    return Number(value.displayRating).toFixed(2);
  }
  if (value.displayKind === 'BAND' && value.displayBand?.trim()) {
    return value.displayBand.trim();
  }
  return null;
}

export function ratingPilotHistoryHeadline(value: RatingPilotHistorySnapshot): string | null {
  if (value.displayKind === 'NUMERIC' && Number.isFinite(value.displayRating)) {
    return Number(value.displayRating).toFixed(2);
  }
  if (value.displayKind === 'BAND' && value.displayBand?.trim()) {
    return value.displayBand.trim();
  }
  return null;
}

export function ratingPilotJourneyPoints(history?: PlayerPilotRatingHistory): RatingJourneyPoint[] {
  if (!history?.available) return [];
  return history.periods.flatMap(period => {
    const value = period.current.displayRating;
    if (period.current.displayKind !== 'NUMERIC' || !Number.isFinite(value)) return [];
    return [{
      throughPeriod: period.throughPeriod,
      value: Number(value),
      restated: period.current.restated || period.priorVersions.length > 0,
    }];
  });
}

export function ratingPilotEvidenceLabel(state: RatingPilotEvidenceState | null): string | null {
  return state ? EVIDENCE_LABELS[state] : null;
}

export function ratingPilotIntervalLabel(value: PlayerPilotRating): string | null {
  if (!Number.isFinite(value.interval80Lower) || !Number.isFinite(value.interval80Upper)) {
    return null;
  }
  return `${Number(value.interval80Lower).toFixed(2)} - ${Number(value.interval80Upper).toFixed(2)}`;
}

export function ratingPilotSnapshotLabel(value: string | null): string | null {
  if (!value) return null;
  const recordedAt = new Date(value);
  if (Number.isNaN(recordedAt.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(recordedAt);
}

export function ratingPilotThroughLabel(value: string | null): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

export function ratingPilotScaleBand(value: PlayerPilotRating): RatingScaleBand | null {
  if (value.displayKind !== 'NUMERIC' || !Number.isFinite(value.displayRating)) return null;

  const markerPercent = scalePercent(Number(value.displayRating));
  if (!Number.isFinite(value.interval80Lower) || !Number.isFinite(value.interval80Upper)) {
    return { markerPercent, intervalLeftPercent: null, intervalWidthPercent: null };
  }

  const lower = scalePercent(Math.min(Number(value.interval80Lower), Number(value.interval80Upper)));
  const upper = scalePercent(Math.max(Number(value.interval80Lower), Number(value.interval80Upper)));
  return {
    markerPercent,
    intervalLeftPercent: lower,
    intervalWidthPercent: Math.max(upper - lower, 0),
  };
}

export function ratingPilotSurfaceState({
  loading,
  rating,
  error,
  offline = false,
  paused = false,
}: RatingPilotSurfaceInput): RatingPilotSurfaceState {
  const hasPublishedRating = Boolean(
    rating?.available
    && ratingPilotHeadline(rating)
    && ratingPilotEvidenceLabel(rating.evidenceState),
  );
  if (loading && !hasPublishedRating) return 'loading';
  if (paused) return 'paused';
  if (hasPublishedRating && error) return offline ? 'stale_offline' : 'stale_failed';
  if (hasPublishedRating) return 'ready';
  if (error) return offline ? 'offline' : 'failed';
  return 'unavailable';
}

function scalePercent(value: number): number {
  const clamped = Math.min(Math.max(value, RATING_SCALE_MIN), RATING_SCALE_MAX);
  return ((clamped - RATING_SCALE_MIN) / (RATING_SCALE_MAX - RATING_SCALE_MIN)) * 100;
}
